import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AxiosError } from "axios";
import GenericCard from "@ui-components/Card/GenericCard";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import {
  GenericTable,
  GenericTableColumn,
} from "@ui-components/Table/GenericTable";
import GenericSearchbar from "@ui-components/Searchbar/GenericSearchbar";
import { TableSkeleton } from "@ui-components/Skeleton/GenericSkeleton";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import ErrorFetchingItems from "@shared/components/ErrorFetching";
import {
  buildPredicate,
  ParsedFilter,
} from "kontrolEdge/helpers/advancedFilters";
import { normalizeFilters } from "kontrolEdge/helpers/normalizeFilters";
import { ReactComponent as IconEdit } from "@assets/Icons/Icon_Edit.svg";
import { ReactComponent as IconDelete } from "@assets/Icons/Icon_Delete.svg";
import { ReactComponent as IconEye } from "@assets/Icons/Icon_Eye.svg";
import { ReactComponent as IconPlus } from "@assets/Icons/IconPlusNV.svg";
import {
  CarrierBridgeRow,
  carrierBridgeColumns,
  shapeCarrierBridgeRows,
} from "./CarrierBridgeData";
import { makeCarrierBridgeFields } from "./carrierBridgeFieldSpec";
import { carrierBridgeAdminText } from "./carrierBridgeLabels";
import { describeCarrierBridgeError } from "./carrierBridgeForm";
import CarrierBridgeModal, { CarrierBridgeModalMode } from "./CarrierBridgeModal";
import {
  CarrierOption,
  PortOption,
  deleteCarrierBridge,
  fetchAvailableCarriers,
  fetchCarrierBridgeList,
  fetchPortOptions,
} from "./request/request";

const deleteText = carrierBridgeAdminText.delete;

const actionButtonClass =
  "flex items-center justify-center w-10 h-10 rounded border border-semantic-border-secondary text-semantic-text-secondary hover:bg-semantic-state-active";
const deleteButtonClass =
  "flex items-center justify-center w-10 h-10 p-2 rounded border border-semantic-border-secondary bg-semantic-bg-danger-secondary text-semantic-text-danger hover:opacity-90";

interface ModalState {
  mode: CarrierBridgeModalMode;
  row: CarrierBridgeRow | null;
}

/**
 * Admin Center → Kontrol Edge → Carrier Bridge.
 *
 * Lists every tenant's Carrier Bridge services, drafts included, and creates,
 * edits and deletes them (CTX-723 / CTX-724). Unlike the product lists, an
 * empty result keeps the table on screen instead of `EmptyItems`: the admin
 * needs the create action to add the first one.
 */
const CarrierBridgePage: React.FC = () => {
  const api = useAxiosAuth0();
  const [rows, setRows] = useState<CarrierBridgeRow[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [activeFilters, setActiveFilters] = useState<ParsedFilter[]>([]);

  const [modal, setModal] = useState<ModalState | null>(null);
  const [ports, setPorts] = useState<PortOption[]>([]);
  const [carriers, setCarriers] = useState<CarrierOption[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [optionsLoaded, setOptionsLoaded] = useState(false);

  const [rowToDelete, setRowToDelete] = useState<CarrierBridgeRow | null>(null);
  const [deleteBlockedMessage, setDeleteBlockedMessage] = useState<string | null>(null);

  const fields = useMemo(() => makeCarrierBridgeFields(), []);
  const suggestedKeys = useMemo(() => fields.map((f) => f.label), [fields]);

  const normalized = useMemo(
    () =>
      normalizeFilters<CarrierBridgeRow>(activeFilters, fields, {
        defaultStringToContains: true,
        quotedStringsExact: true,
      }),
    [activeFilters, fields]
  );
  const predicate = useMemo(
    () => buildPredicate<CarrierBridgeRow>(normalized, fields),
    [normalized, fields]
  );
  const filteredRows = useMemo(
    () => rows.filter(predicate),
    [rows, predicate]
  );

  const loadList = useCallback(async () => {
    setIsFetching(true);
    setFetchError(null);
    try {
      const { status, data } = await fetchCarrierBridgeList(api);
      if (status >= 500 || data.success === false) {
        setFetchError(data.message || "Unknown server error");
        setRows([]);
        return;
      }
      setRows(Array.isArray(data.data) ? shapeCarrierBridgeRows(data.data) : []);
    } catch (err) {
      console.error("Error fetching Carrier Bridge services", err);
      const message =
        err instanceof AxiosError
          ? err.response?.data?.message ?? err.message
          : "Network error";
      setFetchError(message);
      setRows([]);
    } finally {
      setIsFetching(false);
      setHasFetched(true);
    }
  }, [api]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  // Ports and carriers are only needed by the form, so they load the first
  // time it opens rather than with the page.
  const ensureFormOptions = useCallback(async () => {
    if (optionsLoaded || optionsLoading) return;
    setOptionsLoading(true);
    try {
      const [portsResponse, carriersResponse] = await Promise.all([
        fetchPortOptions(api),
        fetchAvailableCarriers(api),
      ]);
      setPorts(Array.isArray(portsResponse.data.data) ? portsResponse.data.data : []);
      setCarriers(Array.isArray(carriersResponse.data.data) ? carriersResponse.data.data : []);
      setOptionsLoaded(true);
    } catch (err) {
      console.error("Error loading Carrier Bridge form options", err);
    } finally {
      setOptionsLoading(false);
    }
  }, [api, optionsLoaded, optionsLoading]);

  const openModal = useCallback(
    (mode: CarrierBridgeModalMode, row: CarrierBridgeRow | null) => {
      setModal({ mode, row });
      if (mode !== "view") ensureFormOptions();
    },
    [ensureFormOptions]
  );

  const confirmDelete = async () => {
    if (!rowToDelete) return;
    const row = rowToDelete;
    setRowToDelete(null);
    try {
      await deleteCarrierBridge(api, row.id);
      await loadList();
    } catch (err) {
      // An active subscription (or any other refusal) is explained, not
      // swallowed: the service is still there and the admin needs to know why.
      console.error("Error deleting Carrier Bridge", err);
      setDeleteBlockedMessage(describeCarrierBridgeError(err).message);
    }
  };

  const columns = useMemo<GenericTableColumn<CarrierBridgeRow>[]>(
    () => [
      ...carrierBridgeColumns,
      {
        header: "Actions",
        id: "actions",
        renderCell: (row) => (
          <div className="flex items-center gap-2">
            <button
              type="button"
              className={actionButtonClass}
              onClick={() => openModal("view", row)}
              title="View details"
              aria-label={`View ${row.serviceId}`}
            >
              <IconEye className="w-5 h-5" />
            </button>
            <button
              type="button"
              className={actionButtonClass}
              onClick={() => openModal("edit", row)}
              title="Edit"
              aria-label={`Edit ${row.serviceId}`}
            >
              <IconEdit className="w-9 h-9" />
            </button>
            <button
              type="button"
              className={deleteButtonClass}
              onClick={() => setRowToDelete(row)}
              title="Delete"
              aria-label={`Delete ${row.serviceId}`}
            >
              <IconDelete className="w-5 h-5" />
            </button>
          </div>
        ),
      },
    ],
    [openModal]
  );

  const showInitialSkeleton = !hasFetched || (isFetching && rows.length === 0);

  if (fetchError) return <ErrorFetchingItems text={fetchError} />;

  return (
    <GenericCard isCollapsible={false}>
      <div className="mb-4 p-5 bg-semantic-bg-primary">
        <div className="flex items-end justify-between">
          <div className="w-[520px] my-5">
            <GenericSearchbar
              placeholder={carrierBridgeAdminText.searchPlaceholder}
              suggestedKeys={suggestedKeys}
              onFiltersChange={setActiveFilters}
              liveFiltering
              debounceMs={200}
            />
          </div>
          <div className="mb-10">
            <GenericButton
              label={
                <div className="flex items-center gap-2">
                  <IconPlus className="w-5 h-5" />
                  <span>{carrierBridgeAdminText.addButton}</span>
                </div>
              }
              variant="brand"
              size="md"
              onClick={() => openModal("create", null)}
            />
          </div>
        </div>
      </div>

      <div className="px-5 mb-5">
        {/* The table's own slot in skeleton. The overlay this replaces covered
            the rail and the views bar for the whole wait (CTX-1125). */}
        {showInitialSkeleton ? (
          <TableSkeleton columns={columns.length} rows={5} />
        ) : (
          <GenericTable
            columns={columns}
            data={filteredRows}
            initialRowsPerPage={10}
            rowsPerPageOptions={[10, 20, 30]}
            tableClassName="bg-semantic-bg-primary"
            width="100%"
          />
        )}
      </div>

      <CarrierBridgeModal
        isOpen={modal !== null}
        mode={modal?.mode ?? "create"}
        service={modal?.row?.raw ?? null}
        ports={ports}
        carriers={carriers}
        optionsLoading={optionsLoading}
        api={api}
        onClose={() => setModal(null)}
        onSaved={loadList}
      />

      <GenericAlert
        isOpen={rowToDelete !== null}
        onClose={() => setRowToDelete(null)}
        onConfirm={confirmDelete}
        title={deleteText.title}
        description={deleteText.description(rowToDelete?.serviceId ?? "")}
        cancelLabel={deleteText.cancel}
        confirmLabel={deleteText.confirm}
        variant="confirm"
      />

      <GenericAlert
        isOpen={deleteBlockedMessage !== null}
        onClose={() => setDeleteBlockedMessage(null)}
        onConfirm={() => setDeleteBlockedMessage(null)}
        title={deleteText.blockedTitle}
        description={deleteBlockedMessage ?? ""}
        confirmLabel={deleteText.blockedConfirm}
        variant="warning"
        hideCancel
      />
    </GenericCard>
  );
};

export default CarrierBridgePage;
